import React from "react";
import { Link, useParams } from "react-router-dom";
// import Quote from "./Quote";
import { findRoute, findService, findSubcategory } from "./serviceData";

const ServiceRoutePage = () => {
  const params = useParams();
  const { categorySlug, serviceSlug } = params;
  const service = findService(categorySlug, serviceSlug);
  const isNestedRoute = Boolean(params.routeSlug);
  const subCategorySlug = isNestedRoute ? params.subCategorySlug : undefined;
  const routeSlug = isNestedRoute
    ? params.routeSlug
    : service?.singlePage
      ? serviceSlug
      : service?.routes?.some((item) => item.slug === params.subCategorySlug)
        ? params.subCategorySlug
        : undefined;
  const subcategory = findSubcategory(service, subCategorySlug);
  const route = service?.singlePage
    ? {
      title: service.title,
      intro: service.description,
      chapters: service.chapters,
    }
    : findRoute(service, subCategorySlug, routeSlug);

  if (!service || !route) {
    return (
      <div className="bg-white px-4 pb-24 pt-40">
        <div className="container mx-auto max-w-3xl text-center">
          <h1 className="text-4xl font-bold text-[rgb(26,36,58)]">
            Service route not found
          </h1>
          <p className="mt-4 text-gray-600">
            The requested service route does not match the available service
            data.
          </p>
          <Link
            to="/services/immigration-law"
            className="mt-8 inline-flex bg-[#C9A04A] px-6 py-3 font-bold text-[rgb(26,36,58)]"
          >
            View immigration services
          </Link>
        </div>
      </div>
    );
  }

  const chapters = Array.isArray(route.chapters) ? route.chapters : [];

  const getChapterTitle = (chapter) =>
    typeof chapter === "string" ? chapter : chapter.title;

  const getChapterBody = (chapter) =>
    typeof chapter === "string"
      ? "We review the relevant requirements, identify the evidence needed and help you present the facts clearly so your application or matter is easy to assess."
      : chapter.body;

  const handleAnchorClick = (event, targetId) => {
    event.preventDefault();
    const target = document.getElementById(targetId);

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.pushState(null, "", `#${targetId}`);
    }
  };

  return (
    <div className="scroll-smooth bg-white pt-20">
      <section className="relative overflow-hidden bg-[rgb(26,36,58)] py-8 text-white lg:pt-20">
        <img
          src="/theroyalcourtofjustice.png"
          alt=""
          className="absolute -right-16 top-8 hidden opacity-10 lg:block"
        />
        <div className="container relative mx-auto px-4">
          <div className="w-100">
            <div className="mb-6 flex flex-wrap items-center gap-3 text-sm font-semibold text-[#C9A04A]">
              <Link to="/" className="hover:text-white">
                Home
              </Link>
              <span>/</span>
              <Link
                to={`/services/${categorySlug}`}
                className="hover:text-white"
              >
                {service.category}
              </Link>
              <span>/</span>
              <Link
                to={`/services/${categorySlug}/${serviceSlug}`}
                className="hover:text-white"
              >
                {service.title}
              </Link>
              <span>/</span>
              {subcategory && (
                <>
                  <Link
                    to={`/services/${categorySlug}/${serviceSlug}/${subcategory.slug}`}
                    className="hover:text-white"
                  >
                    {subcategory.title}
                  </Link>
                  <span>/</span>
                </>
              )}
              <span>{route.title}</span>
            </div>
            <h1 className="mb-6 text-4xl font-bold leading-tight lg:text-6xl">
              {route.title}
            </h1>
            <p className="w-100 text-base leading-8 text-white/85 lg:text-lg">
              {route.intro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#contact"
                className="inline-flex items-center justify-center bg-[#C9A04A] px-6 py-3 font-bold text-[rgb(26,36,58)] hover:bg-white"
              >
                Get advice
              </a>
              <a
                href="tel:+4401216306635"
                className="inline-flex items-center justify-center border border-white/40 px-6 py-3 font-bold text-white hover:border-yellow hover:text-[#C9A04A]"
              >
                Call +44 (0) 121 630 6635
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-panel">
            <h2 className="text-2xl font-bold text-[rgb(26,36,58)]">
              Eligibility, Requirements & How to Apply
            </h2>
            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {chapters.map((chapter, index) => (
                <a
                  key={getChapterTitle(chapter)}
                  href={`#chapter-${index + 1}`}
                  onClick={(event) =>
                    handleAnchorClick(event, `chapter-${index + 1}`)
                  }
                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-[rgb(26,36,58)] transition hover:bg-white"
                >
                  <span className="mr-2 text-purple">{index + 1}.</span>
                  {getChapterTitle(chapter)}
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {chapters.map((chapter, index) => (
              <section
                id={`chapter-${index + 1}`}
                key={getChapterTitle(chapter)}
                className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-8 shadow-panel"
              >
                <div className="flex flex-col gap-4 md:flex-row md:gap-6">
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center bg-purple font-bold text-white">
                    {index + 1}
                  </span>
                  <div>
                    <h2 className="text-2xl font-bold text-[rgb(26,36,58)]">
                      {getChapterTitle(chapter)}
                    </h2>
                    <p className="mt-3 leading-8 text-gray-700">
                      {getChapterBody(chapter)}
                    </p>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* <Quote /> */}
    </div>
  );
};

export default ServiceRoutePage;
